import React, { createContext, useContext, useEffect, useState } from "react";

import { apiFetch } from "../apiBindings";
import { PageContext } from "../components/shared/Page";
import {
    storageLocal as storage,
    storageSession as session,
} from "../utils/StorageHandler";

export const DataContext = createContext();

/**
 * Fetch the prices from the api and store them into the result.
 */
export const fetchProducts = async (zipcode, consumption, tarifcode, ort) => {
    const verbandsnummer = storage.get("gas:verbandsnummer");
    return apiFetch(
        `/gas/${zipcode}/${consumption}/${verbandsnummer}/${tarifcode}/${ort}`
    ).then((response) => response.text());
};

const GasDataProvider = ({ children }) => {
    const pageContext = useContext(PageContext);
    const { updateLastCalculator } = pageContext.actions;
    /**
     * Returns the place from local storage.
     * Repairs the value, if it is set the old way.
     */

    const getPlaceFromLocalStorage = (fallbackReturn) => {
        const value = storage.get("gas:place");
        // If it is not set already, return default
        if (value === null) {
            return fallbackReturn;
        } else if (value.includes("{")) {
            return storage.getJson("gas:place");
        } else {
            return fallbackReturn;
        }
    };

    /**
     * Returns the street from local storage.
     * Repairs the value, if it is set the old way.
     */
    const getStreetFromLocalStorage = (fallbackReturn) => {
        const value = storage.get("gas:street");
        // If it is not set already, return default
        if (value === null) {
            return fallbackReturn;
        } else if (value.includes("{")) {
            return storage.getJson("gas:street");
        } else {
            return fallbackReturn;
        }
    };

    /**
     * Zipcode
     */
    const [zipcode, setZipcode] = useState(
        storage.get("gas:zipcode") ? storage.get("gas:zipcode") : ""
    );

    /**
     * Consumption
     */
    const [consumption, setConsumption] = useState(
        storage.get("gas:consumption") || 5000
    );

    const [place, setPlace] = useState(
        getPlaceFromLocalStorage({
            value: "Bitte wählen",
            label: "Bitte wählen",
            value2: {
                ortsname: "Bitte wählen",
            },
        })
    );
    const [placePart, setPlacePart] = useState(
        storage.get("gas:placePart") || "Bitte wählen"
    );

    const [placeList, setPlaceList] = useState({
        value: "Bitte wählen",
        label: "Bitte wählen",
    });

    const [selectedStreet, setSelectedStreet] = useState(
        getStreetFromLocalStorage({
            value: "Bitte wählen",
            label: "Bitte wählen",
        })
    );
    const [streetSelectList, setStreetSelectList] = useState({
        value: "Bitte wählen",
        label: "Bitte wählen",
    });

    const [houseNum, setHouseNum] = useState(
        storage.get("gas:housenumber") || ""
    );

    const [verbandsnummer, setVerbandsnummer] = useState(
        storage.get("gas:verbandsnummer") || ""
    );

    const [showStreetInput, setShowStreetInput] = useState(true);
    const [showHouseNumberInput, setShowHouseNumberInput] = useState(true);
    const [showPlaceInput, setShowPlaceInput] = useState(true);

    /**
     * Name of the product
     */
    const [chosenTarifName, setChosenTarifName] = useState();
    /**
     * Result with prices
     */
    const [result, setResult] = useState(null);

    /**
     * Loading state
     */
    const [loading, setLoading] = useState(false);

    /**
     * form Errors
     */
    const [formErrors, setFormErrors] = useState([]);

    /**
     * Chosen product
     */
    const [productChoice, setProductChoice] = useState(
        storage.getInt("gas:product-choice", 1)
    );

    const [zipcodeIsValid, setZipcodeIsValid] = useState(true);
    const [checkingForZipcode, setCheckingForZipcode] = useState(false);

    const [consumptionIsValid, setConsumptionIsValid] = useState(true);
    const [checkingForConsumption] = useState(false);

    useEffect(() => {
        startup();
    }, []);

    /**
     * Sync zipcode with local storage.
     */
    useEffect(() => {
        storage.set("gas:zipcode", zipcode);
        calculateIfPossible();
    }, [zipcode]);

    /**
     * Sync consumption with local storage.
     */
    useEffect(() => {
        storage.set("gas:consumption", consumption);
    }, [consumption]);

    /**
     * Sync consumption with local storage.
     */
    useEffect(() => {
        storage.setJson("gas:street", selectedStreet);
    }, [selectedStreet]);

    /**
     * Sync consumption with local storage.
     */
    useEffect(() => {
        storage.setJson("gas:place", place);
        if (
            zipcode &&
            zipcode.length > 0 &&
            place &&
            place.value.length > 0 &&
            place.value.indexOf("Bitte") !== 0
        ) {
            getStreets(zipcode, place);
        }
    }, [place]);

    /**
     * Sync consumption with local storage.
     */
    useEffect(() => {
        storage.set("gas:housenumber", houseNum);
    }, [houseNum]);

    /**
     * Sync up the local storage, when the result changes.
     */
    useEffect(() => {
        if (result !== null) {
            syncProductInfos();
        }
    }, [result]);

    /**
     * store verbandsnummer
     */
    useEffect(() => {
        storage.set("gas:verbandsnummer", verbandsnummer);
    }, [verbandsnummer]);

    /**
     * Sync product choice with local storage.
     */
    useEffect(() => {
        storage.set("gas:product-choice", productChoice);
        syncProductInfos();
    }, [productChoice]);

    /**
     * Sync the product infos into the local storage.
     */
    const syncProductInfos = () => {
        if (result && result.products.length > 0) {
            const chosenProduct = getProductWithPrices();
            storage.set("gas:tarif-id", chosenProduct.tarifID);
            storage.set("gas:tarif-code", chosenProduct.tarifCode);
            storage.set("gas:tarif-name", chosenProduct.tarifName);
            storage.set(
                "gas:total-price",
                chosenProduct.gesamtpreisOhneBoniBrutto
            );
            storage.set(
                "gas:tarif-name-original",
                chosenProduct.tarifNameOrigin
            );
            storage.set("gas:base-price", chosenProduct.grundpreisBrutto);

            setChosenTarifName(chosenProduct.tarifName);
        }
    };

    /**
     * Startup call.
     */
    const startup = async () => {
        // Here we can do some startup stuff.
    };

    /**
     * Calculate if the configuration is valid.
     */
    const calculateIfPossible = () => {
        if (zipcode && zipcode.length === 5) {
            getPlaces(zipcode);
        }
    };

    /**
     * Clear the checkout before we go in the order again.
     */
    const clearCheckout = () => {
        storage.remove("gas:checkout");
        storage.remove("gas:order");
        storage.remove("gas:delivery-details");
        storage.remove("gas:delivery-address");
        storage.remove("gas:billing-address");
        storage.remove("gas:iban");

        session.remove("gas:checkout");
        session.remove("gas:order");
        session.remove("gas:delivery-details");
        session.remove("gas:delivery-address");
        session.remove("gas:result");
    };

    /**
     *
     */

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const calculate = async () => {};

    const calculatorFormValidataion = () => {
        setFormErrors([]);
        if (place.value === "Bitte wählen" || place === null) {
            setFormErrors(["place"]);
            return false;
        }
        if (
            selectedStreet.value === "Bitte wählen" ||
            selectedStreet === null
        ) {
            setFormErrors(["street"]);
            return false;
        }
        if (houseNum === "") {
            setFormErrors(["housenumber"]);
            return false;
        }

        return true;
    };

    /**
     * Fetch the prices from the api and store them into the result.
     */
    const calculateStreet = async (localZipcode, localConsumption) => {
        if (!calculatorFormValidataion()) {
            setLoading(false);
            setResult(null);
            storeResultToStorage(null);
            return false;
        }
        if (!loading) {
            // do the calculation
            setLoading(true);
            setResult(null);
            storeResultToStorage(null);
            const localHouseNumber = houseNum === "" ? "null" : houseNum;
            const localStreet =
                selectedStreet.value === "Bitte wählen"
                    ? "null"
                    : selectedStreet.value;
            const localPlace =
                place.value === "Bitte wählen" || place.value === ""
                    ? "null"
                    : place.value;
            const localPlacePart =
                placePart === "Bitte wählen" || placePart === ""
                    ? "null"
                    : placePart;

            getOrtsInfo(
                localZipcode,
                localPlace,
                localPlacePart,
                localStreet,
                localHouseNumber
            )
                .then((data) => {
                    if (data.exception && data.exception.length > 0) {
                        throw "error in get result";
                    }
                    if (data.gasNetzgebietLength === 1 && data.verbandsnummer) {
                        // setLoading(true);
                        // setShowStreetInput(false)
                        setVerbandsnummer(data.verbandsnummer);
                        getPrices(
                            localZipcode,
                            localConsumption,
                            data.verbandsnummer,
                            "null",
                            place.value
                        );
                    } else if (data.gasNetzgebietLength > 1) {
                        setLoading(false);
                        setResult(null);
                        storeResultToStorage(null);
                    } else if (data.gasNetzgebietLength === 0) {
                        setLoading(false);
                        setResult(null);
                        storeResultToStorage(null);
                    }
                })
                .catch(() => {
                    setResult(null);
                    storeResultToStorage(null);
                    setLoading(false);
                    setStreetSelectList(null);
                    setHouseNum("");
                    setSelectedStreet(null);
                    setPlaceList(null);
                    setPlace(null);
                });
        }
    };

    const storeResultToStorage = (data) => {
        session.setJson("gas:result", data);
    };

    const getPlaces = async (localZipcode) => {
        let response = await apiFetch(
            `/getAg/gas/getOrteByPlzAsSelect/${localZipcode}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }
        );
        let json = await response.json();
        if (json.length === 0) {
            setZipcodeIsValid(false);
        } else if (json.length > 0) {
            setZipcodeIsValid(true);
            setPlaceList(json);
        }
        return json;
    };

    const getStreets = async (localZipcode, localPlace) => {
        let response = await apiFetch(
            `/getAg/gas/getStrassenByPlzOrtAsSelect/${localZipcode}/${localPlace.value}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }
        );
        let json = await response.json();
        setStreetSelectList(json);
        return json;
    };

    const getOrtsInfo = async (
        localZipcode,
        localPlace,
        localPlacePart,
        localStreet,
        localHouseNumber
    ) => {
        let response = await apiFetch(
            `/getAg/gas/getOrtsinfoExtendedPlus/${localZipcode}/${localPlace}/${localPlacePart}/${localStreet}/${localHouseNumber}/extended/false`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }
        );
        let json = await response.json();
        return json;
    };

    const getPrices = async (
        localZipcode,
        localConsumption,
        localVerbandsnummer,
        localTarifcode,
        localOrt
    ) => {
        updateLastCalculator("erdgas");

        let response = await apiFetch(
            `/gas/${localZipcode}/${localConsumption}/${localVerbandsnummer}/${localTarifcode}/${localOrt}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }
        );
        let json = await response.json();

        setResult(json);
        storeResultToStorage(json);
        setLoading(false);

        syncProductInfos();
    };

    /**
     * Checks if the zipcode is valid
     * @param {*} localZipcode
     * @param {*} consumption
     */
    const checkZipcode = async (localZipcode, consumption = 5000) => {
        setCheckingForZipcode(true);

        const response = await apiFetch(
            `/gascheckresult/${localZipcode}/${consumption}`
        );
        const data = await response.json();
        setZipcodeIsValid(data.is_valid);
        setCheckingForZipcode(false);
    };

    /**
     * Checks if the consumption for the given zipcode is valid.
     * @param {*} localZipcode
     * @param {*} localConsumption
     */
    const checkConsumption = async (localZipcode, localConsumption) => {
        setCheckingForZipcode(true);

        const response = await apiFetch(
            `/gascheckresult/${localZipcode}/${localConsumption}`
        );
        const data = await response.json();
        setConsumptionIsValid(data.is_valid);
        setCheckingForZipcode(false);
    };

    /**
     * Check if the given key is present in the errors.
     *
     * @param {*} key
     */
    const hasFormErrorAt = (key) => {
        return formErrors.filter((item) => item === key).length > 0;
    };

    /**
     * Remove an element from the errors.
     *
     * @param {*} key
     */
    const clearFormError = (key) => {
        setFormErrors(formErrors.filter((item) => item !== key));
    };

    /**
     * Returns the result object for the selected product.
     */
    const getProductWithPrices = () => {
        return result.products[productChoice];
    };

    const data = {
        zipcode,
        consumption,
        result,
        loading,
        chosenTarifName,
        productChoice,
        place,
        placePart,
        placeList,
        houseNum,
        zipcodeIsValid,
        checkingForZipcode,
        consumptionIsValid,
        checkingForConsumption,
        showStreetInput,
        streetSelectList,
        selectedStreet,
        showHouseNumberInput,
        showPlaceInput,
        formErrors,
    };

    const actions = {
        setZipcode,
        setConsumption,
        setProductChoice,
        calculate,
        calculateStreet,
        clearCheckout,
        setResult,
        checkZipcode,
        checkConsumption,
        getProductWithPrices,
        setSelectedStreet,
        setShowHouseNumberInput,
        setHouseNum,
        setStreetSelectList,
        setShowStreetInput,
        setPlace,
        setPlacePart,
        setPlaceList,
        setShowPlaceInput,
        getPlaces,
        getStreets,
        clearFormError,
        hasFormErrorAt,
        setFormErrors,
    };

    return (
        <DataContext.Provider
            value={{
                data,
                actions,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};
export default GasDataProvider;
